import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getFirestoreInstance,
  getAuthInstance,
  waitForAuthBridge,
  collection,
  doc,
  addDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  type DocumentData,
} from '@/lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import type { Firestore } from 'firebase/firestore';

// GIF metadata from Giphy
export interface GifData {
  giphyId: string;
  url: string;
  previewUrl: string;
  width: number;
  height: number;
}

// Track metadata for sharing music from league submissions
export interface TrackData {
  submissionId: number;
  roundId: number;
  roundName: string;
  title: string;
  artistName: string;
  albumName: string | null;
  thumbnailUrl: string | null;
  spotifyUrl: string | null;
  appleMusicUrl: string | null;
  youtubeMusicUrl: string | null;
}

// League metadata for sharing leagues in chat
export interface LeagueData {
  leagueId: number;
  name: string;
  description: string | null;
  headerImageUrl: string | null;
  themeColor: string;
  type: "music" | "video";
  category: string | null;
  participants: number;
  maxParticipants: number;
  isPublic: boolean;
  inviteCode: string | null;
  status: string;
  ownerUsername: string | null;
}

export interface ChatMessage {
  id: string;
  userId: string;
  username: string;
  avatarUrl: string | null;
  content: string;
  contentType: 'text' | 'image' | 'gif' | 'track' | 'league' | 'system';
  imageUrl: string | null;
  gifData: GifData | null;
  trackData: TrackData | null;
  leagueData: LeagueData | null;
  isDeleted: boolean;
  deletedBy: string | null;
  deletedAt: Date | null;
  createdAt: Date;
  replyToId: string | null;
}

interface UseLeagueChatOptions {
  leagueId: number;
  userId: string | null;
  username: string | null; // Can be null when profile not yet loaded
  avatarUrl: string | null;
  enabled?: boolean;
  messageLimit?: number;
}

interface SendMessageOptions {
  content: string;
  contentType?: 'text' | 'image' | 'gif' | 'track';
  imageUrl?: string;
  gifData?: GifData;
  trackData?: TrackData;
}

interface UseLeagueChatReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (options: SendMessageOptions) => Promise<void>;
  isSending: boolean;
}

/**
 * Hook to manage real-time chat messages using Firestore
 */
export function useLeagueChat({
  leagueId,
  userId,
  username,
  avatarUrl,
  enabled = true,
  messageLimit = 100,
}: UseLeagueChatOptions): UseLeagueChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const firestoreRef = useRef<Firestore | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Initialize Firestore and set up listener
  useEffect(() => {
    console.log(`📱 [useLeagueChat useEffect] entered - enabled: ${enabled}, leagueId: ${leagueId}`);
    if (!enabled || !leagueId) {
      console.log(`📱 [useLeagueChat useEffect] early return - enabled: ${enabled}, leagueId: ${leagueId}`);
      setIsLoading(false);
      return;
    }
    console.log(`📱 [useLeagueChat useEffect] proceeding with setup...`);

    let isMounted = true;
    let loadingTimeoutId: NodeJS.Timeout | null = null;

    // Global timeout - if chat doesn't load within 30 seconds, show error
    // This catches any hanging state not covered by individual timeouts
    loadingTimeoutId = setTimeout(() => {
      if (isMounted) {
        console.error('Chat: Global loading timeout reached (30s)');
        setError('Chat took too long to load. Please try again.');
        setIsLoading(false);
      }
    }, 30000);

    const setupListener = async () => {
      const startTime = Date.now();
      const logTiming = (step: string) => {
        console.log(`📱 [Chat Debug] ${step} - ${Date.now() - startTime}ms elapsed`);
      };

      try {
        logTiming('Starting chat setup');
        logTiming(`Platform: ${Capacitor.getPlatform()}, isNative: ${Capacitor.isNativePlatform()}`);

        // On native platforms, wait for the auth bridge to complete first
        // This ensures the web Firebase auth has the user from native auth
        if (Capacitor.isNativePlatform()) {
          logTiming('Waiting for native auth bridge...');
          // Add timeout to prevent hanging on slow/stuck auth bridge
          const authBridgeTimeout = 15000; // 15 seconds max
          try {
            await Promise.race([
              waitForAuthBridge(),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Auth bridge timed out')), authBridgeTimeout)
              ),
            ]);
            logTiming('Auth bridge complete');
          } catch (bridgeError: any) {
            logTiming(`Auth bridge failed/timed out: ${bridgeError?.message}`);
            // Continue anyway - chat may still work or show permission error
          }
        }

        logTiming('Getting Firestore and Auth instances...');
        // Wait for both Firestore and Auth to be ready with timeout
        const initTimeout = 10000; // 10 seconds max
        const [firestore, auth] = await Promise.race([
          Promise.all([
            getFirestoreInstance(),
            getAuthInstance()
          ]),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Firebase initialization timed out')), initTimeout)
          ),
        ]) as [Firestore | null, any];
        logTiming(`Got Firestore: ${!!firestore}, Auth: ${!!auth}`);

        if (!firestore) {
          throw new Error('Firestore not initialized');
        }
        firestoreRef.current = firestore;

        logTiming('Waiting for auth state...');
        // Log current auth state before waiting
        logTiming(`Current auth.currentUser: ${auth.currentUser?.uid ? auth.currentUser.uid.substring(0, 8) + '...' : 'null'}`);

        // Wait for auth state to be determined before setting up listener
        // This ensures Firestore knows about the authenticated user
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            logTiming('Auth state check timed out after 5s, proceeding anyway');
            logTiming(`Final auth.currentUser after timeout: ${auth.currentUser?.uid ? auth.currentUser.uid.substring(0, 8) + '...' : 'null'}`);
            resolve();
          }, 5000);

          const unsubAuth = onAuthStateChanged(auth, (user) => {
            clearTimeout(timeout);
            unsubAuth();
            if (user) {
              logTiming(`Auth confirmed for user: ${user.email}, uid: ${user.uid?.substring(0, 8)}...`);
              resolve();
            } else {
              logTiming('No authenticated user found in onAuthStateChanged callback');
              logTiming(`auth.currentUser at this point: ${auth.currentUser?.uid ? auth.currentUser.uid.substring(0, 8) + '...' : 'null'}`);
              resolve(); // Still resolve - will show permission denied if rules require auth
            }
          }, (error) => {
            clearTimeout(timeout);
            unsubAuth();
            logTiming(`Auth state check error: ${error}`);
            reject(error);
          });
        });

        logTiming('Setting up Firestore listener...');
        const messagesRef = collection(firestore, `leagueChats/${leagueId}/messages`);
        const messagesQuery = query(
          messagesRef,
          orderBy('createdAt', 'desc'),
          limit(messageLimit)
        );
        logTiming('Query created, attaching onSnapshot...');

        // Set up real-time listener
        unsubscribeRef.current = onSnapshot(
          messagesQuery,
          (snapshot) => {
            logTiming(`onSnapshot SUCCESS - got ${snapshot.docs.length} messages`);
            if (!isMounted) return;

            const newMessages: ChatMessage[] = [];
            snapshot.forEach((doc) => {
              const data = doc.data();
              newMessages.push({
                id: doc.id,
                userId: data.userId,
                username: data.username,
                avatarUrl: data.avatarUrl,
                content: data.content,
                contentType: data.contentType || 'text',
                imageUrl: data.imageUrl || null,
                gifData: data.gifData || null,
                trackData: data.trackData || null,
                leagueData: data.leagueData || null,
                isDeleted: data.isDeleted || false,
                deletedBy: data.deletedBy || null,
                deletedAt: data.deletedAt?.toDate() || null,
                createdAt: data.createdAt?.toDate() || new Date(),
                replyToId: data.replyToId || null,
              });
            });

            // Reverse to show oldest first
            setMessages(newMessages.reverse());
            setIsLoading(false);
            setError(null);
            // Clear global timeout on success
            if (loadingTimeoutId) {
              clearTimeout(loadingTimeoutId);
              loadingTimeoutId = null;
            }
          },
          (err) => {
            if (!isMounted) return;
            console.error('Chat listener error:', err);
            // Check for permission denied errors (auth bridge failure)
            if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('PERMISSION_DENIED')) {
              setError('Chat authentication failed. Please sign out and sign back in to access chat.');
            } else {
              setError('Failed to load chat messages');
            }
            setIsLoading(false);
            // Clear global timeout on error
            if (loadingTimeoutId) {
              clearTimeout(loadingTimeoutId);
              loadingTimeoutId = null;
            }
          }
        );
      } catch (err: any) {
        if (!isMounted) return;
        console.error('Chat setup error:', err);
        // Check for permission denied errors (auth bridge failure)
        if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('PERMISSION_DENIED')) {
          setError('Chat authentication failed. Please sign out and sign back in to access chat.');
        } else {
          setError('Failed to connect to chat');
        }
        setIsLoading(false);
        // Clear global timeout on error
        if (loadingTimeoutId) {
          clearTimeout(loadingTimeoutId);
          loadingTimeoutId = null;
        }
      }
    };

    setupListener();

    return () => {
      isMounted = false;
      // Clear global timeout on unmount
      if (loadingTimeoutId) {
        clearTimeout(loadingTimeoutId);
        loadingTimeoutId = null;
      }
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [leagueId, enabled, messageLimit]);

  // Send a message
  const sendMessage = useCallback(
    async (options: SendMessageOptions) => {
      const { content, contentType = 'text', imageUrl, gifData, trackData } = options;

      // Only text messages require content - images, GIFs, and tracks have their own content
      // (imageUrl, gifData, trackData) and the text content is just an optional comment
      const needsContent = contentType === 'text';
      const hasRequiredContent = needsContent ? content.trim() : true;

      // Validate all required fields before sending
      if (!userId || !username || !firestoreRef.current || !hasRequiredContent) {
        console.warn('Chat sendMessage blocked - missing required data:', {
          hasUserId: !!userId,
          hasUsername: !!username,
          hasFirestore: !!firestoreRef.current,
          hasContent: hasRequiredContent,
          contentType
        });
        return;
      }

      setIsSending(true);
      setError(null);

      try {
        const messagesRef = collection(firestoreRef.current, `leagueChats/${leagueId}/messages`);

        // addDoc returns a DocumentReference, we just need to wait for it to complete
        const docRef = await addDoc(messagesRef, {
          userId,
          username,
          avatarUrl,
          content: content.trim().slice(0, 500), // Max 500 chars
          contentType,
          imageUrl: imageUrl || null,
          gifData: gifData || null,
          trackData: trackData || null,
          isDeleted: false,
          deletedBy: null,
          deletedAt: null,
          createdAt: serverTimestamp(),
          replyToId: null,
        });

        // Log success for debugging
        console.log('Message sent successfully:', docRef.id);

        // Send push notifications to other league members (fire and forget)
        // We don't await this to avoid blocking the UI
        const auth = await getAuthInstance();
        const idToken = await auth.currentUser?.getIdToken();
        if (idToken) {
          fetch(`/api/leagues/${leagueId}/chat/notify`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              messagePreview: content.trim().slice(0, 100),
              senderUsername: username,
              contentType: contentType,
              trackTitle: trackData?.title,
              trackArtist: trackData?.artistName,
            }),
          }).catch((err) => {
            // Silently fail - push notifications are non-critical
            console.warn('Failed to send chat push notifications:', err);
          });
        }
      } catch (err: any) {
        console.error('Send message error:', err);

        // Check if this is a Firestore permission error (user is muted)
        if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('PERMISSION_DENIED')) {
          const muteError = new Error('You are muted in this chat and cannot send messages');
          setError('You are muted in this chat');
          throw muteError;
        }

        setError('Failed to send message');
        throw err;
      } finally {
        setIsSending(false);
      }
    },
    [leagueId, userId, username, avatarUrl]
  );

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    isSending,
  };
}
